import { useTenant } from '../contexts/TenantContext';
import { safeGetItem } from '../utils/storage';

class ApiService {
  constructor() {
    // Use API prefix for nginx proxy in production, or direct port access in development
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
    
    // In production, use /api prefix (nginx proxy), in development use direct port
    const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
    this.baseUrl = process.env.REACT_APP_API_GATEWAY_URL || 
      (isDevelopment ? `${protocol}//${hostname}:3001` : `${protocol}//${hostname}/api`);
  }

  async request(endpoint, options = {}) {
    const token = safeGetItem(`minibank_token_${this.getTenantName()}`);
    
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  getTenantName() {
    return window.location.pathname.split('/')[1] || 'test-tenant';
  }

  // User Management APIs
  async getUsers() {
    return this.request(`/${this.getTenantName()}/users`);
  }

  async getUserById(userId) {
    return this.request(`/${this.getTenantName()}/users/${userId}`);
  }

  async createUser(userData) {
    return this.request(`/${this.getTenantName()}/users`, {
      method: 'POST',
      body: userData,
    });
  }

  async updateUser(userId, userData) {
    return this.request(`/${this.getTenantName()}/users/${userId}`, {
      method: 'PUT',
      body: userData,
    });
  }

  async deleteUser(userId) {
    return this.request(`/${this.getTenantName()}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async changePassword(userId, passwordData) {
    return this.request(`/${this.getTenantName()}/users/${userId}/password`, {
      method: 'PUT',
      body: passwordData,
    });
  }

  // Account Management APIs
  async getAccounts() {
    return this.request(`/${this.getTenantName()}/accounts`);
  }

  async getAllAccounts() {
    return this.request(`/${this.getTenantName()}/accounts/all`);
  }

  async getAccountById(accountId) {
    return this.request(`/${this.getTenantName()}/accounts/${accountId}`);
  }

  async getUserAccounts(userId) {
    return this.request(`/${this.getTenantName()}/accounts/user/${userId}`);
  }

  async getDashboardSummary() {
    return this.request(`/${this.getTenantName()}/accounts/dashboard`);
  }

  async createAccount(accountData) {
    return this.request(`/${this.getTenantName()}/accounts`, {
      method: 'POST',
      body: accountData,
    });
  }

  async updateAccount(accountId, accountData) {
    return this.request(`/${this.getTenantName()}/accounts/${accountId}`, {
      method: 'PUT',
      body: accountData,
    });
  }

  async deleteAccount(accountId) {
    return this.request(`/${this.getTenantName()}/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  async getAccountBalance(accountId) {
    return this.request(`/${this.getTenantName()}/accounts/${accountId}/balance`);
  }

  // Transfer APIs
  async createTransfer(transferData) {
    return this.request(`/${this.getTenantName()}/transfers`, {
      method: 'POST',
      body: transferData,
    });
  }

  async getTransfers(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/transfers?${queryString}`
      : `/${this.getTenantName()}/transfers`;
    return this.request(endpoint);
  }

  async getTransferById(transferId) {
    return this.request(`/${this.getTenantName()}/transfers/${transferId}`);
  }

  async getTransferLimits() {
    return this.request(`/${this.getTenantName()}/transfers/limits`);
  }

  async getRemainingLimits() {
    return this.request(`/${this.getTenantName()}/transfers/remaining-limits`);
  }

  // New Phase 4 APIs for external transfers
  async searchAccountByNumber(accountNumber) {
    return this.request(`/${this.getTenantName()}/accounts/search/${accountNumber}`);
  }

  async getFeeInfo(fromAccountId, toAccountId) {
    return this.request(`/${this.getTenantName()}/transfers/fee-info`, {
      method: 'POST',
      body: { fromAccountId, toAccountId },
    });
  }

  async getFeeRules() {
    return this.request(`/${this.getTenantName()}/transfers/fee-rules`);
  }

  // Transaction History APIs
  async getTransactions(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/transactions?${queryString}`
      : `/${this.getTenantName()}/transactions`;
    return this.request(endpoint);
  }

  async getAccountTransactions(accountId, filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/transactions/account/${accountId}?${queryString}`
      : `/${this.getTenantName()}/transactions/account/${accountId}`;
    return this.request(endpoint);
  }

  async getTransactionSummary(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/transactions/summary?${queryString}`
      : `/${this.getTenantName()}/transactions/summary`;
    return this.request(endpoint);
  }

  async exportTransactions(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/transactions/export?${queryString}`
      : `/${this.getTenantName()}/transactions/export`;
    return this.request(endpoint);
  }

  // Notification APIs
  async getNotifications(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    const endpoint = queryString 
      ? `/${this.getTenantName()}/notifications?${queryString}`
      : `/${this.getTenantName()}/notifications`;
    return this.request(endpoint);
  }

  async getUnreadNotificationCount() {
    return this.request(`/${this.getTenantName()}/notifications/unread-count`);
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/${this.getTenantName()}/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async markAllNotificationsAsRead() {
    return this.request(`/${this.getTenantName()}/notifications/mark-all-read`, {
      method: 'POST'
    });
  }

  async deleteNotification(notificationId) {
    return this.request(`/${this.getTenantName()}/notifications/${notificationId}`, {
      method: 'DELETE'
    });
  }

  // Debug APIs (SUPERUSER only)
  async getDebugFlags() {
    return this.request(`/${this.getTenantName()}/debug/flags`);
  }

  async updateDebugFlag(flagName, isEnabled) {
    return this.request(`/${this.getTenantName()}/debug/flags/${flagName}`, {
      method: 'PUT',
      body: { is_enabled: isEnabled },
    });
  }

  async resetDebugFlags() {
    return this.request(`/${this.getTenantName()}/debug/flags/reset`, {
      method: 'POST',
    });
  }

  async checkDebugFlag(flagName) {
    return this.request(`/${this.getTenantName()}/debug/flags/${flagName}/status`);
  }

  // Exchange rates API (external)
  async getExchangeRates() {
    try {
      // Use free exchange rate API
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      
      return {
        success: true,
        base: 'USD',
        rates: {
          EUR: data.rates.EUR,
          GBP: data.rates.GBP,
          JPY: data.rates.JPY,
          CAD: data.rates.CAD
        },
        lastUpdated: data.date
      };
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      // Return mock data if API fails
      return {
        success: false,
        base: 'USD',
        rates: {
          EUR: 0.85,
          GBP: 0.73,
          JPY: 110.25,
          CAD: 1.25
        },
        lastUpdated: new Date().toISOString().split('T')[0],
        error: 'Using cached rates'
      };
    }
  }

}

export const apiService = new ApiService(); 